import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ListTypesAPI } from '@openx/types/inventoryList';
import {
  DomainTargeting,
  type DomainTargetingType,
  type UrlTargeting,
  UrlTargetingType,
} from '@openx/types/targeting/domains';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas';
import { noop } from '@openx/utils/noop';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext';
import { DimensionChip } from '../../shared/DimensionChip/DimensionChip';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle';
import { InventoryListItems } from '../../shared/InventoryListItems/InventoryListItems';
import { Switch } from '../../shared/Switch/Switch';
import { TextFieldEditor } from '../../shared/TextFieldEditor/TextFieldEditor';
import type { TargetingItemsProps } from '../../types';
import { DOMAINS_LIMIT, useBulkAddPlaceholder } from '../constants';
import { useValidate } from '../hooks/useValidate';
import { getDomainsItemsTitle } from '../utils/getDomainsItemsTitle';
import { DomainsTopBox } from './DomainsTopBox';
import { RadioTypeSelector } from './RadioTypeSelector';

export type EditOnlyTargetingItems = {
  handleReset: () => UrlTargeting;
  shouldDisplayResetButton: boolean;
  isResetToAccountEnabled: boolean;
  accountUid?: string;
} & TargetingItemsProps<UrlTargeting>;

export const DomainsItemsEditOnly = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  handleReset = () => ({
    type: UrlTargetingType.blacklist,
    domain_targeting_option: DomainTargeting.ALL_VARIATIONS,
    urls: [],
  }),
  shouldDisplayResetButton = false,
  isResetToAccountEnabled,
  setApplyEnabled,
  accountUid = '',
}: EditOnlyTargetingItems) => {
  const { isOa } = useTargetingContext();
  const { t } = useTranslation();
  const bulkPlaceholder = useBulkAddPlaceholder();
  const { type, domain_targeting_option, urls, inventorylist_id } = targetingParams;
  const [inventoryListItems, setInventoryListItems] = useState<string[]>([]);

  const isBlacklist = type === UrlTargetingType.blacklist;
  const urlsLength = urls?.length ?? 0;
  const currentLimit = DOMAINS_LIMIT - urlsLength;
  const totalDomainsCount = urlsLength + inventoryListItems.length;

  const handleDomainsChange = useCallback(
    (values: string[]) => {
      onChange(prevValues => ({
        ...prevValues,
        urls: values,
      }));
    },
    [onChange]
  );

  const handleClearAll = useCallback(() => {
    onChange({
      type,
      domain_targeting_option,
      urls: undefined,
      inventorylist_id: undefined,
    });
  }, [onChange, type, domain_targeting_option]);

  const handleDomainTargetingTypeChange = useCallback(
    (newTargetingType: DomainTargetingType) => {
      onChange({
        ...targetingParams,
        domain_targeting_option: newTargetingType,
      });
    },
    [onChange, targetingParams]
  );

  const validate = useValidate();

  return (
    <Box
      data-test="sub-section"
      sx={theme => ({
        background: theme.palette.background.boxLight,
        borderBottom: `1px solid ${theme.palette.divider}`,
        margin: theme.spacing(0, -2.5, -1),
        padding: theme.spacing(0.1, 2.5, 1),
      })}
    >
      {isOa && (
        <DomainsTopBox
          onInventoryListChange={onChange}
          inventorylistId={targetingParams.inventorylist_id}
          accountUid={accountUid}
        />
      )}

      <FiltersTitle
        onClear={handleClearAll}
        title={t('Domains [ {count} ]', { count: totalDomainsCount })}
        readonly={readonly}
        marginTopValue={1}
      />

      {!readonly && (
        <Switch
          switchState={isBlacklist}
          onSwitchChange={() =>
            onChange(prevValues => ({
              ...prevValues,
              type: isBlacklist ? UrlTargetingType.whitelist : UrlTargetingType.blacklist,
            }))
          }
          switchOffLabel={t('allow')}
          switchOnLabel={t('block')}
          groupTitle={t('the following:')}
        />
      )}

      {!readonly && (
        <RadioTypeSelector targetingType={domain_targeting_option} onChange={handleDomainTargetingTypeChange} />
      )}

      {isOa && (
        <>
          <InventoryListItems
            onChange={onChange}
            type={type}
            containerStyles={{ marginTop: 2 }}
            setInventoryListItems={setInventoryListItems}
            inventorylistId={inventorylist_id}
            listType={ListTypesAPI.DOMAIN}
            accountUid={accountUid}
          />

          {inventorylist_id && (
            <DimensionChip isInsideOption targetingDimension={isBlacklist ? Criteria.ALL : Criteria.ANY} />
          )}

          <FiltersTitle
            title={getDomainsItemsTitle({
              urlsLength,
              inventoryListItemsLength: inventoryListItems.length,
              isBlacklist,
              isOa,
              t,
            })}
            variant="overline"
            sx={{ color: 'text.secondary' }}
            onClear={() => handleDomainsChange([])}
            marginTopValue={0}
          />
        </>
      )}

      <TextFieldEditor
        validatedTextInput={urls?.join('\n') ?? ''}
        onApply={handleDomainsChange}
        placeholder={bulkPlaceholder}
        validate={validate}
        helperText={t('Enter up to {limit} domains, subdomains, or specific page URLs.', {
          limit: bigNumberWithCommas(currentLimit),
        })}
        limit={DOMAINS_LIMIT}
        handleReset={handleReset}
        inventoryListItems={inventoryListItems || []}
        shouldDisplayResetButton={shouldDisplayResetButton}
        isResetToAccountEnabled={isResetToAccountEnabled}
        inventoryListId={inventorylist_id}
        setApplyEnabled={setApplyEnabled}
        isBlacklist={isBlacklist}
      >
        <Typography
          sx={theme => ({
            color: theme.palette.text.secondary,
            fontSize: theme.typography.body2.fontSize,
            fontWeight: theme.typography.fontWeightRegular,
            lineHeight: theme.typography.caption.lineHeight,
            marginLeft: theme.spacing(1.5),
            marginTop: theme.spacing(1),
          })}
        >
          {!isOa && t('Domains')}
        </Typography>
      </TextFieldEditor>
    </Box>
  );
};
